<template>
  <section id="demand-v2-options-section" class="grid-block">
    <header>
      <span>Варианты</span>
      <VButton
        v-if="!hideCreateOption"
        theme="link"
        :disabled="demandV2.getReasonCannotAddOption() !== null"
        :title="demandV2.getReasonCannotAddOption()"
        @click="addOption"
      >
        Добавить вариант +
      </VButton>
    </header>

    <div class="content">
      <VDataGridV2
        :data="demandV2.options"
        :context-menu="DemandsV2Service.demandOptionContextMenu"
        layout="fill"
        @row:dblclick="editOption"
        @contextMenuItem:done="onContextMenuItemDone"
      >
        <VDataGridColumnV2
          id="is-chosen-option"
          content-align="center"
          width="10px"
        >
          <template #default="{ row }">
            <VLink
              :disabled="demandV2.isApproved"
              @click.stop="toggleOption(row)"
            >
              <template #icon>
              <span class="material-icons">
                {{ row.isChosen ? 'check_circle' : 'radio_button_unchecked' }}
              </span>
              </template>
            </VLink>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="name"
          width="80px"
          content-class="ellipsis"
        >
          <template #label>
            Наименование
            <div class="description">
              Производитель
            </div>
          </template>
          <template #default="{ row }">
            <div
              class="ellipsis"
              :title="row.name"
            >
              {{ row.name }}
            </div>
            <div
              class="ellipsis description"
              :title="row.manufacturer?.name"
            >
              {{ row.manufacturer?.name || '--' }}
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="price"
          width="100px"
          content-align="right"
          content-class="ellipsis"
        >
          <template #label>
            Цена с НДС
            <div class="ellipsis description">
              В валюте предл.
            </div>
          </template>
          <template #default="{ row }">
            <div
              class="ellipsis"
              :title="row.priceFullSeller?.formatInDisplayCurrency(row.offerCurrencyCode, row.getDateForCurrencyRate())"
            >
              {{ row.priceFullSeller?.formatInDisplayCurrency(row.offerCurrencyCode, row.getDateForCurrencyRate()) }}
            </div>
            <div
              class="description"
              :title="row.priceFullSeller?.format(row.offerCurrencyCode)"
            >
              {{ row.priceFullSeller?.format(row.offerCurrencyCode) }}
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="qtyAvailable"
          content-align="right"
          content-class="ellipsis"
          width="100px"
        >
          <template #label>
            Доступное <br> количество
          </template>
          <template #default="{ row }">
            <div
              class="ellipsis"
              :title="row.qtyAvailable.format(row.unit) || '--'"
            >
              {{ row.qtyAvailable.format(row.unit) || '--' }}
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          v-if="demandV2.isChosenMoreThen1Option"
          id="qtySelected"
          content-align="right"
          width="100px"

          @edit="(row, anchor) => onChangeQtySelected(row, anchor)"
        >
          <template #label>
            Выбранное <br> количество
          </template>
          <template #default="{ row }">
            <div class="qty-selected">
              <VIcon
                v-if="row.qtySelected.greaterThan(row.qtyAvailable)"
                material="warning"
                class="qty-exceeded-icon"
                theme="outlined"
                :title="getQtySelectedTitle(row)"
              />

              <div
                class="ellipsis qty-selected-value"
                :title="row.qtySelected?.format(row.unit)"
              >
                {{ row.qtySelected?.format(row.unit) }}
              </div>
            </div>
            <div
              v-if="row.qtySelected.greaterThan(row.qtyAvailable)"
              class="description"
            >
              Выбранное количество превышает доступное
            </div>
          </template>
        </VDataGridColumnV2>
      </VDataGridV2>
    </div>
  </section>
</template>

<style lang="scss">
#demand-v2-options-section {
  td[data-column-id="qtySelected"] {

    > .edit-pencil {
      left: 8px;
      right: unset;
    }
  }

  .qty-selected {
    display:         flex;
    justify-content: flex-end;
  }

  .qty-selected-value {
    width: fit-content;
  }

  .qty-exceeded-icon {
    color: var(--warning-strong);
  }

  .qty-edit-icon {
    color: var(--accent-strong);
  }
}
</style>

<script lang="ts">

import DemandOption                 from '@/domains/DemandsV2/DemandOption';
import DemandV2                     from '@/domains/DemandsV2/DemandV2';
import DemandsV2Service             from '@/domains/DemandsV2/DemandsV2Service';

export default defineComponent({
  name: 'DemandV2OptionsSection',

  props: {
    demandV2: {
      type:     DemandV2,
      required: true,
    },
    hideCreateOption: {
      type:    Boolean,
      default: false,
    },
  },

  /** Варианты изменились — потребность нужно перечитать */
  emits: ['change'],

  setup() {
    return {
      DemandsV2Service,
    };
  },

  methods: {
    /** Подсказка на ячейке выбранного количества. Сравнение через greaterThan:
     *  оператор > на Numeric не работает, valueOf у него кидает. */
    getQtySelectedTitle(row: DemandOption): string {
      if (row.qtySelected.greaterThan(row.qtyAvailable)) {
        return 'Выбранное количество превышает доступное';
      }
      else {
        return '';
      }
    },

    async addOption(): Promise<void> {
      if (await DemandsV2Service.createOption(this.demandV2) === true) {
        this.$emit('change');
      }
    },

    async editOption(option: DemandOption): Promise<void> {
      if (await DemandsV2Service.editOption(option) === true) {
        this.$emit('change');
      }
    },

    /** Грид с :data сам не обновляется — по результату пункта меню перечитываем потребность */
    onContextMenuItemDone(item: unknown, row: DemandOption, result: boolean | void): void {
      if (result === true) {
        this.$emit('change');
      }
    },

    async toggleOption(option: DemandOption): Promise<void> {
      option.choose();
      await DemandsV2Service.updateOption(option);

      this.$emit('change');
    },

    async onChangeQtySelected(row: DemandOption, anchor): Promise<void> {
      this.$core.dialogs.ChangeDemandV2OptionQtyPopup.hideIfOpen()

      if (await this.$core.dialogs.ChangeDemandV2OptionQtyPopup.showAsPromise({ target: anchor, row: row }) === true) {
        this.$emit('change');
      }
    },
  },
});
</script>
